import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Iter-220u → Iter-221 — Production safety self-check.
//
// HISTORY:
//   Iter-220u originally injected a FIXED-POSITION RED BANNER over every
//   page (login, register, marketing, etc.) whenever
//   REACT_APP_BACKEND_URL pointed at a preview / emergent.host host.
//   That banner was intended for admins, but it rendered for EVERY
//   visitor — including prospective talent and clients on the public
//   login screen — making the platform look broken and damaging trust.
//
// CHANGE (Iter-221):
//   The diagnostic is preserved (we MUST know about a prod misconfig)
//   but the public banner is removed. Instead we:
//     1. Log a CRITICAL console.error so any admin / dev with DevTools
//        open sees the misconfig immediately.
//     2. Expose the diagnostic via `window.__IEVNTUR_BACKEND_MISCONFIG__`
//        so the admin-only inline alert in /admin/home + /admin/dashboard
//        can render the warning to authenticated admins ONLY.
//
// The public-facing red banner has been deliberately removed. Do NOT
// reintroduce it — admin alerting must live inside an authenticated
// admin surface only.
(function bootSafetyCheck() {
  try {
    const backend = (process.env.REACT_APP_BACKEND_URL || '').toLowerCase();
    const host = typeof window !== 'undefined' ? (window.location.hostname || '').toLowerCase() : '';
    const unsafeTokens = ['emergent.host', 'preview.emergentagent.com'];
    const backendUnsafe = unsafeTokens.some(t => backend.includes(t));
    const onProductionHost = host === 'app.ievntur.com';
    if (onProductionHost && backendUnsafe) {
      const msg = `[iEvntür CRITICAL] REACT_APP_BACKEND_URL=${process.env.REACT_APP_BACKEND_URL} points at a non-production host while the page is served from app.ievntur.com. Mobile carriers block *.emergent.host — API calls may fail. Fix env var and redeploy.`;
      console.error(msg);
      if (typeof window !== 'undefined') {
        window.__IEVNTUR_BACKEND_MISCONFIG__ = {
          backend: process.env.REACT_APP_BACKEND_URL,
          host,
          message: msg,
          detectedAt: new Date().toISOString(),
        };
      }
    } else if (typeof window !== 'undefined') {
      window.__IEVNTUR_BACKEND_MISCONFIG__ = null;
    }
  } catch (_) { /* never block app boot */ }
})();

export const api = axios.create({
  baseURL: API_BASE_URL,
  // Reasonable default so a stalled request eventually fails fast instead of
  // hanging the UI indefinitely. Slow endpoints (exports, bulk ops) should
  // override this per-request via { timeout: 0 } or a larger value.
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors intelligently.
//
// Previous behavior: ANY 401 from ANY endpoint → force logout + redirect to /login.
// That caused random logouts whenever a background widget hit a permission-gated
// endpoint. Now we only clear the session when we're certain the TOKEN ITSELF is
// invalid — by checking via /auth/me. Permission-denied 401s on feature endpoints
// are propagated to the caller so the page can show a proper error instead of
// nuking the session.
//
// Iter-272 (P0 auth fix) — Recent-login grace window. Two production bugs were
// being caused by the previous interceptor:
//   1. **Double login required** — Login succeeds, the background
//      `authAPI.getMe()` call (which tops up admin_role/permissions) sometimes
//      hits a stale pod or experiences JWT propagation lag and returns 401.
//      The interceptor matched `/auth/me` → wiped localStorage → forced redirect
//      to /login. The user had to log in again.
//   2. **Forced logout during navigation** — Any endpoint that returned 401
//      triggered the verifier (`/auth/me`). On flaky mobile networks the
//      verifier itself could 401 transiently, killing the session.
// Fix: when the user logged in within the last 30 seconds, we suppress the
// auto-logout for /auth/me 401s. Truly stale tokens still get logged out — but
// only after the grace window expires.
const SESSION_ENDPOINTS = ['/auth/me', '/auth/refresh'];
const RECENT_LOGIN_GRACE_MS = 30_000;
let _verifyingToken = false;

// ───── P0.5 (Anita Currie / Dallas incident) — Silent refresh state ─────
//
// On any 401 from a non-refresh endpoint, we try /auth/refresh ONCE
// before declaring the token dead. During the refresh, other 401'd
// requests queue up on the same in-flight promise so we never fire
// parallel duplicate refreshes (each one would invalidate the previous
// via single-session enforcement).
let _refreshInFlight = null;                  // Promise<string|null> — the new token

async function _refreshOnce() {
  const token = localStorage.getItem('token');
  if (!token) return null;
  try {
    const res = await axios.post(
      `${API_BASE_URL}/auth/refresh`,
      {},
      { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 },
    );
    const newToken = res?.data?.token;
    if (newToken) {
      localStorage.setItem('token', newToken);
      // Iter-272 keeps a "last login" marker for its grace window; a
      // refresh is functionally a login for that purpose (fresh token,
      // same session_id) so bump it too.
      localStorage.setItem('_last_login_at', String(Date.now()));
      return newToken;
    }
  } catch (_e) {
    // Fall through — caller will treat null as "refresh failed".
  }
  return null;
}

async function _refreshTokenOrLogout() {
  if (_refreshInFlight) return _refreshInFlight;   // dedup concurrent 401s
  _refreshInFlight = _refreshOnce();
  try {
    return await _refreshInFlight;
  } finally {
    _refreshInFlight = null;
  }
}

function _withinRecentLoginGrace() {
  try {
    const t = parseInt(localStorage.getItem('_last_login_at') || '0', 10);
    return t > 0 && (Date.now() - t) < RECENT_LOGIN_GRACE_MS;
  } catch (_) { return false; }
}

function _forceLogoutRedirect() {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    // Preserve the page the user was on (e.g. a tapped job deep link) so an
    // expired session returns them to it after re-auth instead of dropping
    // them on a dashboard. Only same-origin app paths are ever forwarded.
    const cur = window.location.pathname + (window.location.search || '');
    const safe = cur.startsWith('/') && !cur.startsWith('//') && !cur.includes('://');
    if (safe) {
      try { sessionStorage.setItem('post_auth_redirect', cur); } catch (_e) { /* ignore */ }
    }
    window.location.href = safe ? `/login?redirect=${encodeURIComponent(cur)}` : '/login';
  }
}

// ───── Iter-292e — Deployment stability helpers. ─────
// `_retryAuthMe` re-issues /auth/me up to 3 times with backoff. Covers
// the brief restart window during a deploy where the previous pod is
// shut down but the new one is still warming up. Returns `true` if the
// session recovered, `false` if all retries failed (caller then decides
// whether to logout based on the body / status).
let _authMeRetrying = false;
async function _retryAuthMe() {
  if (_authMeRetrying) return false;
  _authMeRetrying = true;
  const delays = [800, 1800, 3500];  // ~6s total budget
  try {
    for (const wait of delays) {
      await new Promise((r) => setTimeout(r, wait));
      try {
        const r = await api.get('/auth/me');
        if (r?.status === 200) return true;
      } catch (e) {
        // network / 5xx / 401 — keep trying
      }
    }
    return false;
  } finally {
    _authMeRetrying = false;
  }
}

// One-shot toast (or window event for the SW-update banner to consume)
// so the user sees "Server waking up… reconnecting" instead of a silent
// freeze. Debounced — only fires once per 15s so a burst of failing
// background widgets doesn't spam the UI.
//
// Iter-PO2026-06 — the banner used to fire on the FIRST transient blip
// (a single slow poll / one 503 from the ingress), which made it flash
// constantly during normal use. It now only fires after 2+ CONSECUTIVE
// transient failures, and a `server-recovered` event hides it the instant
// any request succeeds again.
let _lastWakingNotifyAt = 0;
let _consecutiveTransientFailures = 0;
function _notifyServerWaking() {
  const now = Date.now();
  if (now - _lastWakingNotifyAt < 15000) return;
  _lastWakingNotifyAt = now;
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('ievntur:server-waking', {
      detail: { at: new Date().toISOString() },
    }));
  } catch (_e) { /* noop */ }
}

function _notifyServerRecovered() {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('ievntur:server-recovered', {
      detail: { at: new Date().toISOString() },
    }));
  } catch (_e) { /* noop */ }
}

api.interceptors.response.use(
  (response) => {
    // Iter-PO2026-06 — any successful response means we're connected again.
    // Clear the transient-failure streak and hide the reconnect banner.
    if (_consecutiveTransientFailures > 0) {
      _consecutiveTransientFailures = 0;
      _notifyServerRecovered();
    }
    // Iter-219 — Contract Story 3 — soft_grace path. The backend signals a
    // pending-agreement warning via the X-Agreement-Warning JSON header on
    // a 2xx response. Surface it to the AgreementGateModal but do not block.
    try {
      const warn = response?.headers?.['x-agreement-warning'] || response?.headers?.['X-Agreement-Warning'];
      if (warn && typeof window !== 'undefined') {
        const parsed = JSON.parse(warn);
        window.dispatchEvent(new CustomEvent('agreement-gate', { detail: parsed }));
      }
    } catch (_e) { /* never break the 2xx path */ }
    return response;
  },
  async (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';

    // Round 2 (ICMA) — hard-gate redirect. When the backend rejects a
    // /apply / /accept / /confirm because the contractor must re-accept
    // the current agreement, take them to /talent/agreement instead of
    // surfacing a naked 403.
    if (status === 403 && error.response?.data?.detail?.code === 'agreement_reaccept_required') {
      try {
        const redirect = error.response.data.detail.redirect || '/talent/agreement';
        if (typeof window !== 'undefined' && window.location.pathname !== redirect) {
          window.location.assign(redirect);
        }
      } catch (_e) { /* fall through — original error surfaces */ }
      return Promise.reject(error);
    }

    // ───── Iter-292e — P0 deployment-stability fix. ─────
    // The pod restarting during a deploy used to look like an
    // authentication failure to the frontend, kicking active users out.
    // We now treat *every* failure mode that ISN'T a definitively-bad
    // token as "backend warming up" and let the next request retry,
    // instead of clearing localStorage + redirecting to /login.
    //
    // What counts as "definitely bad token"?
    //   • HTTP 401 from a session-verification endpoint (/auth/me, /auth/refresh)
    //     AND the response body explicitly says invalid_token / token_expired /
    //     session_expired / invalid_credentials.
    //
    // What counts as "transient deploy/network noise" (DO NOT logout)?
    //   • Network error (no `error.response` — DNS, TCP, etc.)
    //   • 502 / 503 / 504 — proxy / pod restart / gateway
    //   • A 401 whose body doesn't carry a known auth-failure marker
    //     (legacy endpoints sometimes 401 on permission gates)
    //
    // First-hit failures briefly surface a single toast so the user
    // sees "Server waking up… reconnecting" instead of silent freezes.
    const isNetworkError = !error.response;
    const isDeployBounce = status === 502 || status === 503 || status === 504;
    if (isNetworkError || isDeployBounce) {
      // Only surface the "reconnecting" banner after a SUSTAINED outage
      // (2+ consecutive transient failures) so a single slow poll or a
      // one-off ingress 503 never flashes the banner during normal use.
      _consecutiveTransientFailures += 1;
      if (_consecutiveTransientFailures >= 2) {
        _notifyServerWaking();
      }
      return Promise.reject(error);
    }
    // A definitive HTTP response (even a 4xx/500) proves the backend is
    // reachable — reset the streak so we don't falsely stay in "reconnecting".
    if (_consecutiveTransientFailures > 0) {
      _consecutiveTransientFailures = 0;
      _notifyServerRecovered();
    }

    // Iter-219 — hard_block / force_resign 409. Dispatch to the global
    // AgreementGateModal and reject the caller so the page can keep its
    // form state (drafts already auto-saved via saveErrorHandler).
    if (
      status === 409 &&
      error.response?.data?.detail === 'agreement_required' &&
      typeof window !== 'undefined'
    ) {
      try {
        window.dispatchEvent(new CustomEvent('agreement-gate', {
          detail: {
            mode: error.response?.data?.mode || 'hard_block',
            route_key: error.response?.data?.route_key,
            agreements: error.response?.data?.agreements || [],
          },
        }));
      } catch (_e) { /* noop */ }
      return Promise.reject(error);
    }

    // Iter-209 — pending/blocked client → bounce to pending screen.
    if (
      status === 403 &&
      error.response?.data?.detail === 'pending_client_approval' &&
      !window.location.pathname.startsWith('/client/pending')
    ) {
      window.location.href = '/client/pending';
      return Promise.reject(error);
    }

    // iter431 — talent blocked by the ordered onboarding gate → send them to
    // the onboarding hub (always reachable), which shows their exact next step.
    if (
      status === 403 &&
      error.response?.data?.detail === 'onboarding_required' &&
      typeof window !== 'undefined' &&
      window.location.pathname !== '/talent/onboarding'
    ) {
      window.location.href = '/talent/onboarding';
      return Promise.reject(error);
    }

    if (status !== 401) return Promise.reject(error);

    // ───── P0.5 (Anita Currie / Dallas) — Silent refresh + retry. ─────
    //
    // Before we declare the token dead, try to refresh it ONCE. If refresh
    // succeeds we quietly retry the original request with the new token.
    // If refresh fails, we fall through to the existing "verify + logout"
    // logic below so no existing behavior regresses.
    //
    // Skip refresh for the refresh endpoint itself (would loop) and for
    // requests that already carry a `_didRefreshRetry` marker (prevents
    // an infinite loop if the new token also 401s).
    const originalReq = error.config || {};
    const alreadyRetried = originalReq._didRefreshRetry;
    const isRefreshCall = (originalReq.url || '').includes('/auth/refresh');
    // Don't try to refresh on token-dead errors — those need a real login.
    const _detailPreview = String(error.response?.data?.detail || '').toLowerCase();
    const _tokenDeadForRefresh = [
      'refresh_expired', 'invalid_token', 'session_superseded',
      'user_not_found', 'suspended', 'inactive',
      'invalid credentials', 'invalid_credentials',
      'logged in on another device',
    ].some((m) => _detailPreview.includes(m));

    if (!alreadyRetried && !isRefreshCall && !_tokenDeadForRefresh && localStorage.getItem('token')) {
      const newToken = await _refreshTokenOrLogout();
      if (newToken) {
        // Retry the original request with the fresh token. Mark it so
        // we don't recurse if the new token also 401s.
        originalReq._didRefreshRetry = true;
        originalReq.headers = originalReq.headers || {};
        originalReq.headers.Authorization = `Bearer ${newToken}`;
        try {
          return await api.request(originalReq);
        } catch (retryErr) {
          // The retry produced a fresh error — return it directly so
          // the caller sees the accurate status/detail. Don't re-run
          // the token-dead logic (the token is fine, the endpoint just
          // failed for another reason).
          return Promise.reject(retryErr);
        }
      }
      // Refresh failed — fall through to the existing verify/logout logic.
    }

    // Iter-292e — Strict invalid-token detection.
    const detail = String(error.response?.data?.detail || '').toLowerCase();
    const TOKEN_DEAD_MARKERS = [
      'token has expired', 'invalid token', 'token_expired',
      'invalid_token', 'session has expired', 'session_expired',
      'invalid credentials', 'invalid_credentials',
      'logged in on another device',
    ];
    const isTokenDead = TOKEN_DEAD_MARKERS.some((m) => detail.includes(m));

    // If the 401 came from a session endpoint AND the body says the token
    // is dead, the user really is logged out. Otherwise, treat it as
    // transient (let the page render its own error / next refresh retries).
    const isSessionEndpoint = SESSION_ENDPOINTS.some((p) => url.includes(p));
    if (isSessionEndpoint) {
      if (_withinRecentLoginGrace()) {
        console.warn('[auth] /auth/me 401 within recent-login grace — suppressing logout.');
        return Promise.reject(error);
      }
      if (!isTokenDead) {
        // Iter-292e — Cold-start retry. Try /auth/me a couple of times
        // with backoff before declaring the token dead. Covers the
        // ~3-8s window when a deploy is finishing rollout.
        const recovered = await _retryAuthMe();
        if (recovered) return Promise.reject(error);
        _notifyServerWaking();
        return Promise.reject(error);
      }
      _forceLogoutRedirect();
      return Promise.reject(error);
    }

    // For any other endpoint's 401, verify once whether the token is truly dead.
    // If /auth/me succeeds → it's a per-endpoint permission 401, don't log out.
    // If /auth/me also 401s → only log out when the body says the token is dead.
    if (!_verifyingToken && localStorage.getItem('token')) {
      _verifyingToken = true;
      try {
        await api.get('/auth/me');
        // Token is still valid — this was a permission error. Let the caller handle it.
      } catch (verifyErr) {
        const verifyStatus = verifyErr.response?.status;
        const verifyDetail = String(verifyErr.response?.data?.detail || '').toLowerCase();
        const verifyTokenDead = TOKEN_DEAD_MARKERS.some((m) => verifyDetail.includes(m));
        if (verifyStatus === 401 && verifyTokenDead) {
          if (_withinRecentLoginGrace()) {
            console.warn('[auth] verifier 401 within recent-login grace — suppressing logout.');
          } else {
            _forceLogoutRedirect();
          }
        }
        // Network errors / 5xx / non-token 401s = transient, never logout.
      } finally {
        _verifyingToken = false;
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// iter431 — Talent ordered onboarding
export const onboardingAPI = {
  getStatus: () => api.get('/talent/onboarding/status'),
  adminOverview: () => api.get('/admin/talent-onboarding/overview'),
  adminBookedIncomplete: () => api.get('/admin/talent-onboarding/booked-incomplete'),
  adminNotify: (only_booked = true) => api.post('/admin/talent-onboarding/notify', { only_booked }),
  adminGetGate: () => api.get('/admin/talent-onboarding/gate'),
  adminSetGate: (enabled) => api.post('/admin/talent-onboarding/gate', { enabled }),
  // iter432 — mandatory SMS phone-verification kill-switch
  adminGetPhoneGate: () => api.get('/admin/talent-onboarding/phone-gate'),
  adminSetPhoneGate: (enabled) => api.post('/admin/talent-onboarding/phone-gate', { enabled }),
};

// iter433 — On-Hold-for-Payment
export const paymentHoldAPI = {
  myStatus: () => api.get('/payment-hold/my-status'),
  adminApply: (userId, data) => api.post(`/admin/payment-hold/${userId}/apply`, data),
  adminRelease: (userId, data) => api.post(`/admin/payment-hold/${userId}/release`, data),
  adminAchPending: (userId, data) => api.post(`/admin/payment-hold/${userId}/ach-pending`, data),
  adminVerifyAch: (userId, data) => api.post(`/admin/payment-hold/${userId}/verify-ach`, data),
  adminEvaluate: (userId) => api.post(`/admin/payment-hold/${userId}/evaluate`),
  adminState: (userId) => api.get(`/admin/payment-hold/${userId}/state`),
  adminHistory: (userId) => api.get(`/admin/payment-hold/${userId}/history`),
  adminHeld: () => api.get('/admin/payment-hold/held'),
  adminPreview: () => api.get('/admin/payment-hold/preview'),
  adminMissingInvoices: () => api.get('/admin/payment-hold/audit/missing-invoices'),
  adminBackfillInvoices: () => api.post('/admin/payment-hold/audit/backfill-invoices'),
  adminGetSettings: () => api.get('/admin/payment-hold/settings'),
  adminSetSettings: (data) => api.post('/admin/payment-hold/settings', data),
  adminSweep: () => api.post('/admin/payment-hold/sweep'),
};

// Client APIs
export const clientAPI = {
  createShift: (data) => api.post('/client/shifts', data),
  getShifts: () => api.get('/client/shifts'),
  // Iter-314 — Preserve `?as_client=<uuid>` impersonation context so
  // admins previewing as a client don't lose scope when navigating
  // from invoice detail → view event.
  getShiftDetails: (shiftId) => {
    const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const asClient = params?.get?.('as_client');
    return api.get(`/client/shifts/${shiftId}${asClient ? `?as_client=${asClient}` : ''}`);
  },
  bookTalent: (data) => api.post('/client/bookings', data),
  getBookings: () => api.get('/client/bookings'),
  approveHours: (bookingId, hours_worked, rating, feedback) => 
    api.post(`/client/bookings/${bookingId}/approve-hours`, null, {
      params: { hours_worked, rating, feedback }
    }),
};

// Talent APIs
export const talentAPI = {
  getProfile: () => api.get('/talent/profile'),
  submitPremiumProfile: (data) => api.post('/talent/profile/premium', data),
  getAvailableShifts: (params) => api.get('/talent/gigs/available', { params }),
  applyToShift: ({ shift_id, ...rest }) => api.post(`/talent/gigs/${shift_id}/apply`, rest),
  bulkEligibility: (shift_ids) => api.post('/talent/eligibility/bulk', { shift_ids }),
  getApplications: () => api.get('/talent/applications'),
  getBookings: () => api.get('/talent/bookings'),
  checkin: (data) => api.post('/talent/checkin', data),
  checkout: (data) => api.post('/talent/checkout', data),
  getPayouts: () => api.get('/talent/payouts'),
  // Talent ratings (talent rates clients)
  rateClient: (data) => api.post('/talent/ratings', data),
  getPendingRatings: () => api.get('/talent/ratings/pending'),
  getRatingsGiven: () => api.get('/talent/ratings/given'),
};

// Admin APIs
export const adminAPI = {
  getDashboardStats: () => api.get('/admin/dashboard/stats'),
  getPendingPremium: () => api.get('/admin/premium/pending'),
  approvePremium: (premiumId) => api.patch(`/admin/premium/${premiumId}/approve`),
  rejectPremium: (premiumId, reason) => api.patch(`/admin/premium/${premiumId}/reject`, { rejection_reason: reason }),
  getPendingCheckins: () => api.get('/admin/checkins/pending'),
  approveCheckin: (checkinId, notes) => api.patch(`/admin/checkins/${checkinId}/approve`, { admin_notes: notes }),
  getPricingRules: () => api.get('/admin/pricing-rules'),
  createPricingRule: (data) => api.post('/admin/pricing-rules', data),
  getUsers: (params) => api.get('/admin/users', { params }),
  suspendUser: (userId) => api.patch(`/admin/users/${userId}/suspend`),
  activateUser: (userId) => api.patch(`/admin/users/${userId}/activate`),
  getStripeAuditLogs: (limit) => api.get('/admin/audit-logs/stripe', { params: { limit } }),
  getOverrideLogs: (limit) => api.get('/admin/audit-logs/overrides', { params: { limit } }),
  // Two-way ratings (admin view)
  getAllRatings: (params) => api.get('/admin/ratings/all', { params }),
  getRatingsSummary: () => api.get('/admin/ratings/summary'),
};

// Stripe APIs
export const stripeAPI = {
  startOnboarding: () => api.post('/stripe/onboard'),
  getAccountStatus: () => api.get('/stripe/account-status'),
};

// Cloudinary APIs
export const cloudinaryAPI = {
  getSignature: (params) => api.get('/cloudinary/signature', { params }),
};

// Message APIs
export const messageAPI = {
  getThreads: () => api.get('/messages/threads'),
  getMessages: (threadId) => api.get(`/messages/threads/${threadId}`),
  sendMessage: (threadId, data) => api.post(`/messages/threads/${threadId}/messages`, data),
  getNotifications: () => api.get('/messages/notifications'),
  markNotificationRead: (notificationId) => api.patch(`/messages/notifications/${notificationId}/read`),
};

// General APIs
export const generalAPI = {
  getRoles: () => api.get('/roles'),
  getCertifications: () => api.get('/certifications'),
};

// Pricing APIs
export const pricingAPI = {
  getPricingRule: (params) => api.get('/pricing/rules', { params }),
  getRolesByTier: () => api.get('/pricing/roles'),
};

// AI APIs
export const aiAPI = {
  getDressCodePresets: () => api.get('/ai/dress-code-presets'),
  generateDescription: (data) => api.post('/ai/generate-description', data),
  // Iter-133r — dress code + important notes generators.
  generateDressCode: (data) => api.post('/ai/generate-dress-code', data),
  generateImportantNotes: (data) => api.post('/ai/generate-important-notes', data),
};

// Invoice APIs
export const invoiceAPI = {
  getClientInvoices: (params) => api.get('/invoices/client', { params }),
  getClientInvoiceDetail: (id) => api.get(`/invoices/client/${id}`),
  createClientInvoice: (data) => api.post('/invoices/client/create', data),
};

// Clock APIs
export const clockAPI = {
  clockIn: (data, config) => api.post('/clock/in', data, config),
  clockOut: (data, config) => api.post('/clock/out', data, config),
  getStatus: (bookingId) => api.get(`/clock/status/${bookingId}`),
};

// Chat APIs
export const chatAPI = {
  getRooms: () => api.get('/chat/rooms'),
  getMessages: (roomId, params) => api.get(`/chat/rooms/${roomId}/messages`, { params }),
  sendMessage: (roomId, data) => api.post(`/chat/rooms/${roomId}/messages`, data),
  markRead: (roomId) => api.post(`/chat/rooms/${roomId}/mark-read`),
  startPrivateChat: (data) => api.post('/chat/private', data),
  getUnreadCount: () => api.get('/chat/unread-count'),
};

// Quiz APIs
export const quizAPI = {
  create: (data) => api.post('/quizzes/create', data),
  list: () => api.get('/quizzes/list'),
  get: (id) => api.get(`/quizzes/${id}`),
  delete: (id) => api.delete(`/quizzes/${id}`),
  assign: (data) => api.post('/quizzes/assign', data),
  getAssignments: (params) => api.get('/quizzes/assignments/list', { params }),
  removeAssignment: (id) => api.delete(`/quizzes/assignments/${id}`),
  talentPending: () => api.get('/quizzes/talent/pending'),
  submit: (data) => api.post('/quizzes/submit', data),
  results: (params) => api.get('/quizzes/results/all', { params }),
  checkEligibility: (gigId) => api.get(`/quizzes/eligibility/${gigId}`),
};

// Discover APIs
export const discoverAPI = {
  searchTalent: (params) => api.get('/discover/talent', { params }),
  getTalentProfile: (id) => api.get(`/discover/talent/${id}`),
  getRoles: () => api.get('/discover/roles'),
};

// AI Matching APIs
export const matchingAPI = {
  // Client endpoints
  getRecommendedTalent: (shiftId, params) => api.get(`/matching/recommended-talent/${shiftId}`, { params }),
  getQuickMatch: (shiftId) => api.get(`/matching/quick-match/${shiftId}`),
  getApplicantsWithScores: (shiftId) => api.get(`/matching/shift-applicants-scores/${shiftId}`),
  getRosterWithScores: (shiftId) => api.get(`/matching/shift-roster-scores/${shiftId}`),
  getBookingScore: (bookingId) => api.get(`/matching/booking-score/${bookingId}`),
  
  // Talent endpoints
  getRecommendedGigs: (params) => api.get('/matching/recommended-gigs', { params }),
};

// Monetization APIs
export const monetizationAPI = {
  // Highlight Profile
  getHighlightPricing: () => api.get('/monetization/highlight/pricing'),
  getHighlightStatus: () => api.get('/monetization/highlight/status'),
  purchaseHighlight: (duration) => api.post(`/monetization/highlight/purchase/${duration}`),
  activateHighlight: (duration, paymentId) => api.post('/monetization/highlight/activate', null, { params: { duration, payment_id: paymentId } }),
  getHighlightAnalytics: () => api.get('/monetization/highlight/analytics'),
  
  // Featured Jobs
  getFeaturedPricing: () => api.get('/monetization/featured/pricing'),
  purchaseFeaturedJob: (shiftId, boostType) => api.post(`/monetization/featured/purchase/${shiftId}/${boostType}`),
  activateFeaturedJob: (shiftId, boostType, paymentId) => api.post(`/monetization/featured/activate/${shiftId}`, null, { params: { duration: boostType, payment_id: paymentId } }),
  
  // Job Post Pricing
  getJobPostPricing: () => api.get('/monetization/job-post/pricing'),
  
  // Instant Payout
  getPayoutInfo: () => api.get('/monetization/payout/info'),
  getEligiblePayouts: () => api.get('/monetization/payout/eligible'),
  requestPayout: (bookingId) => api.post(`/monetization/payout/request/${bookingId}`),
  getPayoutHistory: () => api.get('/monetization/payout/history'),
};

// Contract APIs
export const contractAPI = {
  // User endpoints
  getPending: () => api.get('/contracts/pending'),
  getAccepted: () => api.get('/contracts/accepted'),
  accept: (data) => api.post('/contracts/accept', data),
  checkCompliance: () => api.get('/contracts/check-compliance'),
  
  // Admin endpoints
  adminList: () => api.get('/contracts/admin/list'),
  adminCreate: (data) => api.post('/contracts/admin/create', data),
  adminUpdate: (id, data) => api.put(`/contracts/admin/${id}`, data),
  adminDeactivate: (id) => api.patch(`/contracts/admin/${id}/deactivate`),
  adminActivate: (id) => api.patch(`/contracts/admin/${id}/activate`),
};

// Operations APIs
export const operationsAPI = {
  getQuickActions: () => api.get('/operations/dashboard/quick-actions'),
  getNoShowCheckNeeded: () => api.get('/operations/no-show-check-needed'),
  detectNoShows: (shiftId, grace) => api.post(`/operations/detect-no-shows/${shiftId}?grace_minutes=${grace || 15}`),
  getPendingTimesheets: (params) => api.get('/operations/pending-timesheets', { params }),
  batchApproveTimesheets: (data) => api.post('/operations/batch-approve-timesheets', data),
  checkLateArrivals: (shiftId, threshold) => api.post(`/operations/check-late-arrivals/${shiftId}?late_threshold_minutes=${threshold || 10}`),
  duplicateShift: (data) => api.post('/operations/duplicate-shift', data),
  bulkDuplicateShift: (shiftId, dates) => api.post(`/operations/bulk-duplicate-shift?shift_id=${shiftId}`, dates),
  calculateOvertime: (bookingId, threshold, multiplier) => api.post(`/operations/calculate-overtime/${bookingId}?overtime_threshold_hours=${threshold || 8}&overtime_multiplier=${multiplier || 1.5}`),
  batchCalculateOvertime: (data) => api.post('/operations/batch-calculate-overtime', data),
};

export default api;